import Promise from 'bluebird';
import _ from 'underscore';
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { InvestImageView } from '@bingads-webui-campaign-react/add-invest-images-view';
import { constants } from '@bingads-webui-campaign-react/image-experience-model';
import { MaxAddAssetJobInParell, ROOT_FOLDER_ID } from '@bingads-webui-campaign-react/asset-library-model';
import { VIEWS } from '../view-type';

const ManagedInvestImageView = ({
  investImageConfig,
  setShowBlocker,
  perfMarker,
  assetLibraryConfig,
  assetLibraryDataService,
  logger,
  maxUploadNum,
  setShowApplyButton,
  triggerAddImagesRef,
  setSelectionInfoText,
  setDisableApplyButton,
  setErrors,
  i18n,
}) => {
  const {
    onImageSelected,
    onImagesSelected,
    onImageAddError,
    enableInvestMultipleSelection: enableMultipleSelection,
  } = assetLibraryConfig;

  const onChangeImageIdSelection = useCallback((args) => {
    const investImagePerfMarker = perfMarker.createChild('investImagePicker');
    investImagePerfMarker.willInit();

    const selectInvestImage = assetLibraryDataService.uploadInvestImage({
      investImageId: args.selectedImageId,
      imageName: args.description,
      imageSrc: args.imageSrc,
      folderId: ROOT_FOLDER_ID,
    })
      .then(value => onImageSelected({ id: value }, constants.imageSources.Invest))
      .catch(e => onImageAddError(e));

    setShowBlocker(true);
    selectInvestImage
      .tap(() => {
        const { selectedImageId: imageId } = args || {};
        logger.logTrace('Ad creation flow - selected invest image', { imageId });
      })
      .finally(() => {
        setShowBlocker(false);
        investImagePerfMarker.done();
      });
  }, [perfMarker, assetLibraryDataService, setShowBlocker, onImageSelected, onImageAddError, logger]);

  const [images, setImages] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [skip, setSkip] = useState(0);
  const [isLoadingFirstPage, setIsLoadingFirstPage] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    // init the state for multiple selection
    if (enableMultipleSelection) {
      setShowApplyButton(true);
      setDisableApplyButton(true);
      setErrors([]);
      setSelectionInfoText(i18n.getString(_TL_('0 of {{limitNumber}} images selected'), { limitNumber: maxUploadNum }));
    }
  }, [enableMultipleSelection, i18n, maxUploadNum, setDisableApplyButton, setErrors, setSelectionInfoText, setShowApplyButton]);

  useEffect(() => {
    const selectedImages = _.filter(images, image => image && image.isSelected) || [];
    if (selectedImages.length > maxUploadNum) {
      setErrors([i18n.getString(_TL_('You\'ve exceeded the max. number of assets you can add at a time ({{limitNumber}}). Please try again.'), { limitNumber: maxUploadNum })]);
      setDisableApplyButton(true);
    } else if (selectedImages.length > 0) {
      setErrors([]);
      setDisableApplyButton(false);
    } else {
      setErrors([]);
      setDisableApplyButton(true);
    }

    const singularImageString = _TL_('{{selectedNumber}} of {{limitNumber}} image selected');
    const pluralImagesString = _TL_('{{selectedNumber}} of {{limitNumber}} images selected');
    const stringToUse = maxUploadNum > 1 ? pluralImagesString : singularImageString;
    setSelectionInfoText(i18n.getString(stringToUse, { selectedNumber: selectedImages.length, limitNumber: maxUploadNum }));
  }, [enableMultipleSelection, i18n, images, maxUploadNum, setDisableApplyButton, setErrors, setSelectionInfoText]);

  useEffect(() => {
    logger.logTrace(`Ad Creation - Navigating to view: ${VIEWS.InvestImage}`);
    return () => {
      logger.logTrace(`Ad Creation - Leaving view: ${VIEWS.InvestImage}`);
    };
  }, [logger]);

  // only for multiple selection mode
  const onApplyButtonClick = useCallback(() => {
    const selectedImages = _.filter(images, image => image && image.isSelected) || [];

    setShowBlocker(true);

    const investImagePerfMarker = perfMarker.createChild('investImagePicker');
    investImagePerfMarker.willInit();

    Promise.map(
      selectedImages,
      (img) => {
        const { id, imageSrc, description } = img || {};
        return assetLibraryDataService.uploadInvestImage({
          investImageId: id,
          imageName: description,
          imageSrc,
          folderId: ROOT_FOLDER_ID,
        });
      },
      { concurrency: MaxAddAssetJobInParell }
    )
      .then((imageIds) => {
        logger.logTrace('Ad creation flow - selected invest images', { imageIds });
        return onImagesSelected(imageIds, constants.imageSources.Invest);
      })
      .catch((err) => {
        onImageAddError(err);
      })
      .finally(() => {
        setShowBlocker(false);
        investImagePerfMarker.done();
      });
  }, [assetLibraryDataService, images, logger, onImageAddError, onImagesSelected, perfMarker, setShowBlocker]);

  // listen to the apply button click for multiple selection
  // eslint-disable-next-line no-param-reassign
  triggerAddImagesRef.current = enableMultipleSelection ? onApplyButtonClick : undefined;

  return (
    <InvestImageView
      {...investImageConfig}
      enableMultipleSelection={enableMultipleSelection}
      onChangeImageIdSelection={enableMultipleSelection ? _.noop : onChangeImageIdSelection}
      imageCellWidth={202}
      imageCellHeight={202}
      images={images}
      setImages={setImages}
      hasMore={hasMore}
      setHasMore={setHasMore}
      skip={skip}
      setSkip={setSkip}
      isLoadingFirstPage={isLoadingFirstPage}
      setIsLoadingFirstPage={setIsLoadingFirstPage}
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
    />
  );
};

ManagedInvestImageView.propTypes = {
  investImageConfig: PropTypes.shape({
    getInvestImages: PropTypes.func,
  }),
  setShowBlocker: PropTypes.func,
  perfMarker: PropTypes.shape({
    willFetchFile: PropTypes.func.isRequired,
    willInit: PropTypes.func.isRequired,
    willFetchData: PropTypes.func.isRequired,
    willRender: PropTypes.func.isRequired,
    done: PropTypes.func.isRequired,
    createChild: PropTypes.func.isRequired,
  }).isRequired,
  assetLibraryDataService: PropTypes.shape({
    uploadInvestImage: PropTypes.func,
  }).isRequired,
  assetLibraryConfig: PropTypes.shape({
    onImageSelected: PropTypes.func,
    onImagesSelected: PropTypes.func,
    onImageAddError: PropTypes.func,
    enableInvestMultipleSelection: PropTypes.bool,
  }).isRequired,
  logger: PropTypes.shape({
    logTrace: PropTypes.func,
  }).isRequired,
  triggerAddImagesRef: PropTypes.shape({
    current: PropTypes.func,
  }).isRequired,
  setShowApplyButton: PropTypes.func,
  setSelectionInfoText: PropTypes.func,
  setDisableApplyButton: PropTypes.func,
  setErrors: PropTypes.func,
  maxUploadNum: PropTypes.number,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
};

ManagedInvestImageView.defaultProps = {
  investImageConfig: {
    getInvestImages: _.noop,
  },
  setShowBlocker: _.noop,
  setShowApplyButton: _.noop,
  setDisableApplyButton: _.noop,
  setSelectionInfoText: _.noop,
  setErrors: _.noop,
  maxUploadNum: 16,
};

export const investImageSourceConfig = {
  title: _TL_('Images from Microsft Invest'),
  showBackButton: true,
  onRenderContent: props => (
    <ManagedInvestImageView
      {...props}
    />
  ),
  buttonConfig: {
    label: _TL_('Microsoft Invest'),
    view: VIEWS.InvestImage,
    iconName: 'ShareMultipleRegular',
    buttonLocator: 'invest-images',
  },
};
